import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, input, output, signal } from '@angular/core';

type LogoSize = 'xsmall' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large' | '3x-large' | '4x-large';

interface LogoErrorEvent {
  imagePath: string;
  error: Error;
  timestamp: Date;
}

interface LogoClickEvent {
  product: 'apollo';
  timestamp: Date;
  nativeEvent: MouseEvent | KeyboardEvent;
}

/**
 * HxApolloLogo - Apollo Product Logo Component
 *
 * Displays the Apollo product logo (currently using ComplexCare logo as placeholder).
 *
 * @example
 *   ```html
 *   <hx-apollo-logo size="medium" />
 *   <hx-apollo-logo [clickable]="true" (logoClick)="handleClick($event)" />
 *   ```;
 */
@Component({
  selector: 'hx-apollo-logo',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (!showFallback()) {
      <img
        [src]="logoPath()"
        [alt]="logoAlt()"
        [ngClass]="logoClasses()"
        [ngStyle]="{ width: logoWidth(), height: logoHeight() }"
        [attr.role]="clickable() ? 'button' : null"
        [attr.tabindex]="clickable() ? tabIndex() : null"
        [attr.aria-label]="ariaLabel() || logoAlt()"
        class="hx-logo__image"
        (error)="onImageError($event)" />
    } @else {
      <div class="hx-logo__fallback" [ngClass]="logoClasses()">
        <span>Apollo</span>
      </div>
    }
  `,
  styleUrl: './logo.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxApolloLogo {
  size = input<LogoSize>('medium');
  width = input<number | undefined>(undefined);
  height = input<number | undefined>(undefined);
  alt = input<string | undefined>(undefined);
  cssClass = input<string>('');
  clickable = input<boolean>(false);
  ariaLabel = input<string | undefined>(undefined);
  tabIndex = input<number>(0);

  logoClick = output<LogoClickEvent>();
  logoError = output<LogoErrorEvent>();

  showFallback = signal(false);

  logoPath = computed(() => '/branding/products/apollo.svg');

  logoAlt = computed(() => this.alt() || 'Apollo Product Logo');

  logoClasses = computed(() => {
    const classes = ['hx-logo', `hx-logo--${this.size()}`];
    if (this.clickable()) classes.push('hx-logo--clickable');
    if (this.cssClass()) classes.push(this.cssClass());
    return classes;
  });

  logoWidth = computed(() => {
    if (this.width()) return `${this.width()}px`;
    const sizeMap = {
      xsmall: '20px',
      small: '24px',
      medium: '48px',
      large: '96px',
      'x-large': '120px',
      '2x-large': '144px',
      '3x-large': '168px',
      '4x-large': '192px',
    };
    return sizeMap[this.size()];
  });

  logoHeight = computed(() => {
    if (this.height()) return `${this.height()}px`;
    const sizeMap = {
      xsmall: '20px',
      small: '24px',
      medium: '48px',
      large: '96px',
      'x-large': '120px',
      '2x-large': '144px',
      '3x-large': '168px',
      '4x-large': '192px',
    };
    return sizeMap[this.size()];
  });

  @HostListener('error', ['$event'])
  onImageError(event: Event) {
    this.showFallback.set(true);
    this.logoError.emit({
      imagePath: this.logoPath(),
      error: new Error('Failed to load logo image'),
      timestamp: new Date(),
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.clickable()) return;
    this.logoClick.emit({
      product: 'apollo',
      timestamp: new Date(),
      nativeEvent: event,
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.clickable()) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.logoClick.emit({
        product: 'apollo',
        timestamp: new Date(),
        nativeEvent: event,
      });
    }
  }
}
